import { useMemo } from 'react';
import { NextPage } from 'next';

import { equipment } from '@r1-frontend/api-domru/full-buy/v1/equipment';
import { IEquipment } from '@r1-frontend/api-domru/full-buy/v1/equipment/dto/Equipment.dto';
import { ICurrentCity } from '@r1-frontend/entities/City/models/ICurrentCity';

import { COLORS } from '@r1-frontend/ui-react/tokens/colors';

import { getBanners, groupByTags, IBanner } from '~/src/api/content/banners';
import { getPageView, IPageView } from '~/src/api/content/page/view';
import { EShopCategory } from '~/src/api/content/shop/getShopItems';
import Layout from '~/src/features/_layout';
import { SeoScriptAggregateRatingWithOffer } from '~/src/features/_layout/SeoScripts';
import Equipment from '~/src/features/equipment';
import { getMinCostProduct } from '~/src/features/equipment/getMinCostProduct';
import { initialProps } from '~/src/helpers/basePageFunc';
import { sortFunction } from '~/src/helpers/functions';
import getRequester from '~/src/helpers/getRequester';
import { getCurrentCity, selectCompanyName } from '~/src/store/selectors/city';
import selectProviderId from '~/src/store/selectors/selectProviderId';

const EXPAND_BANNER = 'bannerInfo.tags,order,routers,routers.labels,routers.props,routers.category';
const ADDITIONAL_OPPORTUNITIES_ID = '2912';
const BANNER_TAGS = ['router_banner', 'router_bonuses'];

interface IPageProps {
    companyName: string,
    currentCity: ICurrentCity,
    productsList: IEquipment[],
    bannersList: IBanner[],
    addOpportunities: IPageView | null,
}

const EquipmentPage: NextPage<IPageProps> = ({
    companyName,
    currentCity,
    productsList,
    bannersList,
    addOpportunities,
}) => {
    const { callCenter, cityIn } = currentCity;

    const products = useMemo(() => {
        return productsList
            .sort(sortFunction)
            .map(item => ({ ...item, alias: item.alias?.replace(/-im$/, '') }));
    }, [productsList]);

    const { router_banner = [], router_bonuses = [] } = useMemo(() => {
        if (bannersList.length) {
            return groupByTags(bannersList, BANNER_TAGS, products);
        }

        return {};
    }, [bannersList, products]);

    const minCostedProduct = getMinCostProduct(products);

    return (
        <>
            <SeoScriptAggregateRatingWithOffer
                {...minCostedProduct}
                name={`Wi-Fi роутеры в ${currentCity.cityIn}`}
                ratingCount={'1971'}
                ratingValue={'4.6'}
            />
            <Layout
                title={`Купить Wi-Fi роутер от ${companyName} в ${cityIn}, цены на оборудование для подключения домашнего интернета`}
                description={`Официальный сайт ${companyName} в ${cityIn}| Оформите заявку на получение Wi-Fi роутера в рассрочку. ` +
                    `Модели и цены на сайте. Оформить заказ: ${callCenter}`}
                withMenu={true}
                withChat={true}
                withFooter={true}
                bgColor={COLORS.BgMain}
            >
                <Equipment
                    city={currentCity}
                    productList={products}
                    banners={router_banner}
                    routersBanners={router_bonuses}
                    addOpportunities={addOpportunities}
                />
            </Layout>
        </>
    );
};

EquipmentPage.getInitialProps = async(options) => {
    await initialProps(options, { isNeedAuthData: true });
    const { req, store } = options;

    const doRequest = getRequester(req);
    const state = store.getState();

    const providerId = selectProviderId(state);
    const currentCity = getCurrentCity(state);
    const companyName = selectCompanyName(state);

    const [
        shopItemsResponse,
        bannersResponse,
        addOpportunitiesResponse,
    ] = await Promise.all([
        doRequest.fullBuy<IEquipment[]>(
            equipment({
                categories: [EShopCategory.Routers],
                providerId,
            }),
        ),
        getBanners({ providerId, tag: BANNER_TAGS, expand: EXPAND_BANNER }),
        getPageView(providerId, ADDITIONAL_OPPORTUNITIES_ID),
    ]);

    return {
        companyName,
        currentCity,
        productsList: shopItemsResponse.isSuccess ? shopItemsResponse.payload : [],
        bannersList: bannersResponse.isSuccess ? bannersResponse.payload : [],
        addOpportunities: addOpportunitiesResponse.isSuccess ? addOpportunitiesResponse.payload : null,
    };
};

export default EquipmentPage;
